// ---------------------------------------------------------------------------
// Reflection pattern surfacing. §5.10 of the UI improvement recommendations
// asks us to replace generic word-frequency "themes" with recognisable
// recurring issues (e.g. "meeting overload", "context switching") that show
// up across a user's last 30 days of entries — the point is "you have flagged
// meeting overload three weeks in a row", not a wordcloud.
//
// Rules these helpers enforce:
//  - A theme only surfaces when it appears on 2+ distinct days in the window
//    (one bad day is not a pattern).
//  - Driver linkage is derived by literal title match against the user's
//    active strategic drivers — no free-text NLP, so a rename of the driver
//    immediately re-anchors every historical reflection.
// ---------------------------------------------------------------------------

export interface ReflectionLike {
  date: string;
  content: string;
  insights?: string | null;
  adjustmentsMade?: string | null;
}

export interface PatternTheme {
  key: string;
  label: string;
  occurrences: number;
  sampleDates: string[];
  /** Concrete action to suggest in response to this pattern. */
  suggestion: PatternSuggestion;
}

export interface PatternSuggestion {
  /** Short imperative verb phrase shown next to the pattern label. */
  text: string;
  /** Internal route to take the user to when they accept the suggestion. */
  href: string;
  /** Label for the button. */
  cta: string;
}

// Action library — keyed by pattern. Each entry maps a recurring complaint to
// a concrete next step the executive can take inside Cadence. Recommendation
// §5.10 second slice: "behavioural nudging — auto-suggest scheduling changes,
// workload feedback, coaching prompts."
const PATTERN_ACTIONS: Record<string, PatternSuggestion> = {
  'meeting-overload': {
    text: 'Block a no-meetings window in your weekly focus pattern.',
    href: '/focus',
    cta: 'Open Focus Time',
  },
  'context-switching': {
    text: 'Schedule longer focus blocks (90+ min) so deep work has room to land.',
    href: '/focus',
    cta: 'Tune focus blocks',
  },
  'focus-lost': {
    text: "Reserve tomorrow's first two hours for protected focus.",
    href: '/focus',
    cta: 'Set focus window',
  },
  'interruptions': {
    text: 'Mark a do-not-disturb window or set OOO for your next deep-work block.',
    href: '/preferences',
    cta: 'Set DND',
  },
  'fatigue': {
    text: 'Open tomorrow and defer non-essential items so the day breathes.',
    href: '/plan',
    cta: 'Replan tomorrow',
  },
  'overcommitted': {
    text: 'Run Replan to redistribute work across the next few days.',
    href: '/plan',
    cta: 'Open Replan',
  },
  'decisions-stuck': {
    text: 'Open the decisions queue and resolve or delegate the blockers.',
    href: '/decisions',
    cta: 'Open decisions',
  },
  'delegation-gap': {
    text: 'Push items down via Delegation — pair drafts with assignees.',
    href: '/delegation',
    cta: 'Open delegation',
  },
};

export interface DriverLike {
  id: string;
  title: string;
}

export interface DriverMention {
  driverId: string;
  driverTitle: string;
  occurrences: number;
}

// The dictionary is deliberately small and executive-focused. Patterns here
// are the ones the recommendation doc calls out by example, plus a handful of
// closely-related synonyms. Extending this list is safe — each entry is
// independent and the matcher is case-insensitive.
const PATTERN_DICTIONARY: { key: string; label: string; phrases: string[] }[] = [
  {
    key: 'meeting-overload',
    label: 'Meeting overload',
    phrases: ['meeting overload', 'too many meetings', 'back to back', 'back-to-back', 'meeting heavy'],
  },
  {
    key: 'context-switching',
    label: 'Context switching',
    phrases: ['context switch', 'context-switch', 'context switching', 'kept switching', 'fragmented day', 'fragmented'],
  },
  {
    key: 'focus-lost',
    label: 'Focus lost',
    phrases: ['no focus time', 'lost focus', 'couldn\u2019t focus', 'could not focus', 'unable to focus', 'deep work lost'],
  },
  {
    key: 'interruptions',
    label: 'Interruptions',
    phrases: ['interrupted', 'interruption', 'drive-by', 'drop-in', 'walked in'],
  },
  {
    key: 'fatigue',
    label: 'Fatigue',
    phrases: ['tired', 'exhausted', 'low energy', 'drained', 'burnt out', 'burned out', 'no energy'],
  },
  {
    key: 'overcommitted',
    label: 'Over-committed',
    phrases: ['over-committed', 'overcommitted', 'over committed', 'spread too thin', 'too much on'],
  },
  {
    key: 'decisions-stuck',
    label: 'Decisions stuck',
    phrases: ['decision stuck', 'blocked on decision', 'waiting on decision', 'need a decision'],
  },
  {
    key: 'delegation-gap',
    label: 'Delegation gap',
    phrases: ['should have delegated', 'delegated too late', 'didn\u2019t delegate', 'did not delegate'],
  },
];

function combinedText(r: ReflectionLike): string {
  return [r.content, r.insights ?? '', r.adjustmentsMade ?? ''].join(' ').toLowerCase();
}

export function detectPatterns(reflections: ReflectionLike[]): PatternTheme[] {
  if (!reflections.length) return [];

  // Map of pattern key → set of dates mentioning it. Using a Set avoids
  // counting two reflections on the same day twice (morning + close-of-day
  // pair shouldn't double-count as a "pattern").
  const hits = new Map<string, { label: string; dates: Set<string> }>();

  for (const r of reflections) {
    const haystack = combinedText(r);
    if (!haystack.trim()) continue;
    for (const { key, label, phrases } of PATTERN_DICTIONARY) {
      if (phrases.some((p) => haystack.includes(p))) {
        if (!hits.has(key)) hits.set(key, { label, dates: new Set() });
        hits.get(key)!.dates.add(r.date);
      }
    }
  }

  const themes: PatternTheme[] = [];
  hits.forEach(({ label, dates }, key) => {
    if (dates.size < 2) return;
    const sampleDates: string[] = [];
    dates.forEach((d) => sampleDates.push(d));
    sampleDates.sort();
    themes.push({
      key,
      label,
      occurrences: dates.size,
      sampleDates,
      // Fall back to a generic prompt if a pattern doesn't have a tailored
      // action — the test fixtures don't cover every key, and we never want
      // a theme to surface without something the user can do about it.
      suggestion: PATTERN_ACTIONS[key] ?? {
        text: 'Bring this up at your next planning review.',
        href: '/plan',
        cta: 'Open plan',
      },
    });
  });

  themes.sort((a, b) => b.occurrences - a.occurrences || a.label.localeCompare(b.label));
  return themes;
}

export function describePattern(theme: PatternTheme): string {
  if (theme.occurrences === 1) return `${theme.label} flagged once.`;
  if (theme.occurrences === 2) return `${theme.label} flagged on 2 days.`;
  return `${theme.label} flagged on ${theme.occurrences} separate days.`;
}

// Words that are too generic to anchor a driver match — if the only thing
// tying a reflection to a driver is "team" or "programme", the linkage is
// more likely to fire a false positive than catch real intent.
const DRIVER_STOP_WORDS = new Set([
  'and', 'the', 'for', 'into', 'with', 'from', 'that', 'this', 'your', 'our',
  'team', 'work', 'plan', 'year', 'make', 'more', 'less', 'across', 'through',
  'business', 'programme', 'program', 'project', 'initiative', 'strategy',
]);

function driverTokens(title: string): string[] {
  return title
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !DRIVER_STOP_WORDS.has(t));
}

export function findDriverMentions(
  reflection: ReflectionLike,
  drivers: DriverLike[],
): DriverMention[] {
  if (!drivers.length) return [];
  const haystack = combinedText(reflection);
  if (!haystack.trim()) return [];

  const mentions: DriverMention[] = [];
  for (const d of drivers) {
    const tokens = driverTokens(d.title);
    if (tokens.length === 0) continue;
    // A driver is "mentioned" if any of its signature tokens appear in the
    // reflection text. Count total token hits across the whole entry so an
    // entry that mentions "EMEA" three times ranks above one that brushes
    // past it once.
    let occurrences = 0;
    for (const tok of tokens) {
      // split-length substring counting avoids regex escaping for tokens
      // that might contain numbers (e.g. "fy26").
      occurrences += haystack.split(tok).length - 1;
    }
    if (occurrences > 0) {
      mentions.push({ driverId: d.id, driverTitle: d.title, occurrences });
    }
  }
  return mentions;
}
